import { BaseSocketService, SocketConnectionConfig } from './baseSocketService';
import {
  ApplicantData,
  ReportData,
  NoReportState,
  OwnerCandidate,
  ErrorSeverity,
  ErrorAction,
} from 'components/AiChat/types';

export interface AiResponseStarted {
  requestId: string;
  sessionId?: string;
}

export interface AiResponseDelta {
  requestId: string;
  text: string;
}

export interface AiResponseWidget {
  requestId: string;
  widget: { type: string; data: Record<string, any> };
}

export interface AiResponseComplete {
  requestId: string;
  sessionId: string;
  cost?: number;
  usage?: { input_tokens: number; output_tokens: number };
  messageId?: string;
}

export interface AiResponseError {
  requestId: string;
  code?: string;
  message?: string;
  error?: string;
  retryAfterMs?: number;
}

export interface AiCardApplicantFound {
  cardId?: string;
  data: ApplicantData;
}

export interface AiCardReportFound {
  cardId?: string;
  data: ReportData;
}

export interface AiCardNoReport {
  cardId?: string;
  applicationId: string;
  applicationNumber?: number;
  ownerIndex: number;
  ownerName: string;
  state: NoReportState;
  missingFields?: string[];
}

export type OwnerPickerReason = 'ambiguous' | 'no_exact_match';

export interface AiCardOwnerPicker {
  cardId?: string;
  applicationId: string;
  applicationNumber: number;
  rawNeedle: string;
  candidates: OwnerCandidate[];
  reason?: OwnerPickerReason;
}

export interface AiResolvePending {
  sessionId: string;
}

export interface AiCardAgentLoading {
  cardId?: string;
}

export interface AiCardErrorTerminal {
  cardId?: string;
  text: string;
  severity?: ErrorSeverity;
  applicationId?: string;
  applicationNumber?: number;
  actions?: ErrorAction[];
}

export interface AiSystemMessage {
  cardId?: string;
  text: string;
  spinner?: boolean;
}

export type AiActionType = 'end_turn' | 'retry_analyze' | 'pick_owner';

export interface AiChatCallbacks {
  onStarted: (data: AiResponseStarted) => void;
  onDelta: (data: AiResponseDelta) => void;
  onWidget: (data: AiResponseWidget) => void;
  onComplete: (data: AiResponseComplete) => void;
  onError: (data: AiResponseError) => void;
  onApplicantFound: (data: AiCardApplicantFound) => void;
  onReportFound: (data: AiCardReportFound) => void;
  onNoReport: (data: AiCardNoReport) => void;
  onOwnerPicker: (data: AiCardOwnerPicker) => void;
  onAgentLoading: (data: AiCardAgentLoading) => void;
  onErrorCard: (data: AiCardErrorTerminal) => void;
  onSystemMessage: (data: AiSystemMessage) => void;
  onResolvePending: (data: AiResolvePending) => void;
}

export class AiAnalysisSocketService extends BaseSocketService {
  private callbacks: AiChatCallbacks | null = null;
  private boundListeners = false;

  constructor() {
    super('AiAnalysis');
  }

  async connect(config: SocketConnectionConfig, callbacks: AiChatCallbacks): Promise<void> {
    this.callbacks = callbacks;
    const socket = await this.getSocket(config);

    if (!this.boundListeners) {
      socket.on('ai:response:started', (data: AiResponseStarted) => this.callbacks?.onStarted(data));
      socket.on('ai:response:delta', (data: AiResponseDelta) => this.callbacks?.onDelta(data));
      socket.on('ai:response:widget', (data: AiResponseWidget) => this.callbacks?.onWidget(data));
      socket.on('ai:response:complete', (data: AiResponseComplete) => this.callbacks?.onComplete(data));
      socket.on('ai:response:error', (data: AiResponseError) => this.callbacks?.onError(data));
      socket.on('ai:error', (data: { error: string }) => {
        this.callbacks?.onError({ requestId: '', error: data.error });
      });

      socket.on('ai:card:applicant_found', (data: AiCardApplicantFound) => this.callbacks?.onApplicantFound(data));
      socket.on('ai:card:report_found', (data: AiCardReportFound) => this.callbacks?.onReportFound(data));
      socket.on('ai:card:no_report', (data: AiCardNoReport) => this.callbacks?.onNoReport(data));
      socket.on('ai:card:owner_picker', (data: AiCardOwnerPicker) => this.callbacks?.onOwnerPicker(data));
      socket.on('ai:card:agent_loading', (data: AiCardAgentLoading) => this.callbacks?.onAgentLoading(data));
      socket.on('ai:card:error', (data: AiCardErrorTerminal) => this.callbacks?.onErrorCard(data));
      socket.on('ai:system:message', (data: AiSystemMessage) => this.callbacks?.onSystemMessage(data));
      socket.on('ai:card:resolve_pending', (data: AiResolvePending) => this.callbacks?.onResolvePending(data));

      this.boundListeners = true;
      this.incrementSubscriptions();
    }
  }

  sendMessage(message: string, sessionId?: string): void {
    if (!this.socket?.connected) return;
    this.socket.emit('ai:message', { message, sessionId });
  }

  sendAction(action: AiActionType, payload: Record<string, any>): void {
    if (!this.socket?.connected) return;
    this.socket.emit(`ai:action:${action}`, payload);
  }

  cancelRequest(requestId: string): void {
    if (!this.socket?.connected) return;
    this.socket.emit('ai:cancel', { requestId });
  }

  listSessions(
    callback: (data: { sessions: any[]; nextCursor?: string | null }) => void,
    options?: { query?: string; cursor?: string; limit?: number },
  ): void {
    if (!this.socket?.connected) return;
    this.socket.once('ai:history:sessions', callback);
    this.socket.emit('ai:history:list', options ?? {});
  }

  loadSession(sessionId: string, callback: (data: { sessionId: string; cards?: any[]; messages?: any[] }) => void): void {
    if (!this.socket?.connected) return;
    this.socket.once('ai:history:messages', callback);
    this.socket.emit('ai:history:load', { sessionId });
  }

  deleteSession(sessionId: string, callback: (data: { sessionId: string }) => void): void {
    if (!this.socket?.connected) return;
    this.socket.once('ai:history:deleted', callback);
    this.socket.emit('ai:history:delete', { sessionId });
  }

  getCurrentSession(
    callback: (data: {
      sessionId: string | null;
      title: string | null;
      locked: boolean;
      activeRequestId?: string | null;
    }) => void,
  ): void {
    if (!this.socket?.connected) return;
    this.socket.once('ai:session:current', callback);
    this.socket.emit('ai:session:current');
  }

  startNewSession(
    options?: { fresh?: boolean },
  ): Promise<{
    sessionId: string;
    title: string | null;
    isEmpty: boolean;
  }> {
    return new Promise((resolve, reject) => {
      if (!this.socket?.connected) {
        reject(new Error('Socket not connected'));
        return;
      }
      const timer = setTimeout(() => {
        reject(new Error('ai:session:new timed out'));
      }, 10_000);
      this.socket.once('ai:session:created', (data) => {
        clearTimeout(timer);
        resolve(data);
      });
      this.socket.emit('ai:session:new', options ?? {});
    });
  }

  loadSessionAsync(sessionId: string): Promise<{ sessionId: string; cards?: any[]; messages?: any[] }> {
    return new Promise((resolve, reject) => {
      if (!this.socket?.connected) {
        reject(new Error('Socket not connected'));
        return;
      }
      const timer = setTimeout(() => reject(new Error('ai:history:load timed out')), 10_000);
      this.socket.once('ai:history:messages', (data) => {
        clearTimeout(timer);
        resolve(data);
      });
      this.socket.emit('ai:history:load', { sessionId });
    });
  }

  protected onReconnection(): void {
    this.logger.debug('Reconnected to AI analysis');
  }

  override disconnect(): void {
    this.boundListeners = false;
    this.callbacks = null;
    super.disconnect();
  }
}

export const aiAnalysisSocketService = new AiAnalysisSocketService();
